import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  HandHelping, Users, UserPlus, Plus, Search, CheckCircle, Clock,
  FileText, Calendar, Target, Star, Phone, Mail, MapPin,
  TrendingUp, Award, Layers, Timer, Flag, Heart,
  Briefcase, Hash, Eye, Shield, Zap, Gift
} from "lucide-react";

export default function VolunteerPage({ user }) {
  const [tab, setTab] = useState("volunteers");
  const [triggers, setTriggers] = useState({ volunteer: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "volunteers", label: "المتطوعون", icon: Users },
    { key: "teams", label: "الفرق", icon: Layers },
    { key: "opportunities", label: "الفرص التطوعية", icon: Zap },
    { key: "hours", label: "ساعات التطوع", icon: Timer },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={HandHelping} title="التطوع" subtitle="إدارة المتطوعين والفرق والفرص التطوعية" />

      {perms.canEdit && (
        <div style={vq.row}>
          <button style={vq.btn("#059669")} onClick={() => fireQuick("volunteers", "volunteer")}>
            <span style={vq.emoji}>🙋</span><span>إضافة متطوع جديد</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "volunteers" && <VolunteersTab canEdit={perms.canEdit} triggerOpen={triggers.volunteer} />}
      {tab === "teams" && <TeamsTab canEdit={perms.canEdit} />}
      {tab === "opportunities" && <OpportunitiesTab canEdit={perms.canEdit} />}
      {tab === "hours" && <HoursTab />}
      {tab === "meetings" && <MeetingsTab departmentName="التطوع" />}
      {tab === "transactions" && <TransactionsTab budget={150000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="volunteer" />}
      {tab === "reports" && <ReportsTab departmentName="التطوع" />}
    </div>
  );
}

const vq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: (color) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 16px", borderRadius: 10,
    background: `${color}10`, color, border: `1px solid ${color}30`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 700,
    cursor: "pointer", transition: "all 0.15s",
  }),
  emoji: { fontSize: 16 },
};

/* ─── Volunteers Tab ─── */
const VOL_NATIONALITIES = ["سعودي", "مصري", "سوري", "يمني", "أردني", "فلسطيني", "هندي", "باكستاني", "أخرى"];
const VOL_GENDERS = ["ذكر", "أنثى"];
const VOL_MARITAL = ["أعزب", "متزوج"];
const VOL_EDUCATION = ["ثانوي", "دبلوم", "بكالوريوس", "ماجستير", "دكتوراه"];
const VOL_TEAMS = ["فريق الإفطار", "فريق المساجد", "فريق الجاليات", "فريق الإعلام", "فريق النسائي", "فريق الطوارئ"];
const VOL_INTERESTS = ["دعوة", "تعليم", "إفطار", "صيانة", "إعلام", "تنظيم", "إدارة", "تصميم", "ترجمة", "قيادة"];
const VOL_AVAILABILITY = ["صباح أيام العمل", "مساء أيام العمل", "نهاية الأسبوع", "رمضان", "مناسبات فقط"];
const VOL_YESNO = ["نعم", "لا"];

const INITIAL_VOL = {
  // personal
  name: "", nationalId: "", nationality: "", birthDate: "",
  gender: "", maritalStatus: "", education: "", specialty: "", workplace: "",
  // contact
  phone: "", email: "", city: "", district: "",
  // volunteering
  team: "", skills: "", priorExperience: "",
  interests: [], availability: [],
  hasDrivingLicense: "لا", hasCar: "لا",
  notes: "",
};

const RATING_LEVELS = ["ممتاز", "جيد", "مقبول", "ضعيف"];
const RATING_SCORE = { "ممتاز": 4, "جيد": 3, "مقبول": 2, "ضعيف": 1 };
const RECOMMENDATIONS = ["يُرقّى لقائد فريق", "يستمر", "يحتاج تدريب", "لا يُوصى بالاستمرار"];
const INITIAL_EVAL = {
  periodFrom: "", periodTo: "",
  punctuality: "جيد", workQuality: "جيد", teamwork: "جيد",
  initiative: "جيد", appearance: "جيد",
  hours: "", achievements: "", improvements: "",
  recommendation: "يستمر", evaluatorNotes: "", evaluator: "",
};

function VolunteersTab({ canEdit, triggerOpen = 0 }) {
  const [data, setData] = useState(DEMO_VOLUNTEERS);
  const [search, setSearch] = useState("");
  const [filterTeam, setFilterTeam] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEval, setShowEval] = useState(false);
  const [evalForm, setEvalForm] = useState(INITIAL_EVAL);
  const [form, setForm] = useState(INITIAL_VOL);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  function computeAvg() {
    const scores = [
      RATING_SCORE[evalForm.punctuality],
      RATING_SCORE[evalForm.workQuality],
      RATING_SCORE[evalForm.teamwork],
      RATING_SCORE[evalForm.initiative],
      RATING_SCORE[evalForm.appearance],
    ];
    const avg = scores.reduce((s, x) => s + x, 0) / scores.length;
    return { avg, percent: Math.round((avg / 4) * 100) };
  }

  function handleSaveEval() {
    if (!evalForm.periodFrom || !evalForm.periodTo) {
      showToast("فترة التقييم مطلوبة", "error");
      return;
    }
    const { avg, percent } = computeAvg();
    const evaluation = {
      id: `ev${Date.now()}`,
      volunteerId: selected.id,
      periodFrom: evalForm.periodFrom,
      periodTo: evalForm.periodTo,
      punctuality: evalForm.punctuality,
      workQuality: evalForm.workQuality,
      teamwork: evalForm.teamwork,
      initiative: evalForm.initiative,
      appearance: evalForm.appearance,
      avgScore: avg,
      percentScore: percent,
      hours: Number(evalForm.hours) || 0,
      achievements: evalForm.achievements,
      improvements: evalForm.improvements,
      recommendation: evalForm.recommendation,
      evaluatorNotes: evalForm.evaluatorNotes,
      evaluator: evalForm.evaluator,
      date: new Date().toISOString().slice(0, 10),
    };
    setData(prev => prev.map(v =>
      v.id === selected.id
        ? { ...v, evaluations: [...(v.evaluations || []), evaluation] }
        : v
    ));
    setSelected(prev => prev ? { ...prev, evaluations: [...(prev.evaluations || []), evaluation] } : prev);
    setEvalForm(INITIAL_EVAL);
    setShowEval(false);
    showToast("تم حفظ التقييم بنجاح");
  }

  function toggleArrField(field, value) {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }));
  }

  function handleSave() {
    if (!form.name || !form.phone) {
      showToast("الاسم ورقم الجوال مطلوبان", "error");
      return;
    }
    const newV = {
      id: `v${Date.now()}`,
      name: form.name,
      nationalId: form.nationalId,
      nationality: form.nationality,
      birthDate: form.birthDate,
      gender: form.gender,
      maritalStatus: form.maritalStatus,
      education: form.education,
      specialty: form.specialty,
      workplace: form.workplace,
      phone: form.phone,
      email: form.email,
      city: form.city,
      district: form.district,
      team: form.team || "—",
      skills: form.skills ? form.skills.split(/،|,/).map(s => s.trim()).filter(Boolean) : [],
      priorExperience: form.priorExperience,
      interests: [...form.interests],
      availability: [...form.availability],
      hasDrivingLicense: form.hasDrivingLicense === "نعم",
      hasCar: form.hasCar === "نعم",
      notes: form.notes,
      hours: 0,
      joinDate: new Date().toISOString().slice(0, 7),
      status: "active",
      evaluations: [],
    };
    setData(prev => [...prev, newV]);
    setForm(INITIAL_VOL);
    setShowAdd(false);
    showToast("تمت إضافة المتطوع بنجاح");
  }

  const teams = [...new Set(data.map(v => v.team))];
  const filtered = data.filter(v => {
    const ms = v.name.includes(search) || v.skills.join(",").includes(search);
    const mt = filterTeam === "all" || v.team === filterTeam;
    return ms && mt;
  });

  const VOL_STATUSES = [
    { key: "active", label: "نشط", color: C.success },
    { key: "inactive", label: "غير نشط", color: C.textLight },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="إجمالي المتطوعين" value={data.length} icon={Users} color="#059669" />
        <KPICard title="نشطون" value={data.filter(v => v.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="ساعات التطوع" value={data.reduce((s, v) => s + v.hours, 0).toLocaleString()} icon={Timer} color={C.teal} />
        <KPICard title="الفرق" value={teams.length} icon={Layers} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={S.toolbar}>
          <div style={S.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو المهارة..." value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)} style={S.filterSelect}>
              <option value="all">جميع الفرق</option>
              {teams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة متطوع</Btn>}
          </div>
        </div>
      </Card>

      <div style={S.volGrid}>
        {filtered.map(v => (
          <Card key={v.id} style={S.volCard} onClick={() => setSelected(v)}>
            <div style={S.volTop}>
              <div style={S.volAvatar}>{v.name.charAt(0)}</div>
              <StatusBadge status={v.status} statuses={VOL_STATUSES} />
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{v.name}</h4>
            <p style={{ margin: 0, fontSize: 12, color: "#059669", fontWeight: 600 }}>{v.team}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              {v.skills.slice(0, 3).map((sk, i) => <span key={i} style={S.skillTag}>{sk}</span>)}
            </div>
            <div style={S.volStats}>
              <span><Timer size={12} /> {v.hours} ساعة</span>
              <span><Calendar size={12} /> منذ {v.joinDate}</span>
            </div>
            {v.hours >= 100 && <div style={S.starBadge}><Star size={12} /> متطوع متميز</div>}
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`متطوع — ${selected.name}`} onClose={() => setSelected(null)} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={S.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={S.detailRow}><strong>الفريق:</strong> {selected.team}</div>
            {selected.nationality && <div style={S.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>}
            {selected.phone && <div style={S.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>}
            {selected.email && <div style={S.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>}
            {selected.city && <div style={S.detailRow}><strong>المدينة:</strong> {selected.city}{selected.district ? ` — ${selected.district}` : ""}</div>}
            {selected.education && <div style={S.detailRow}><strong>المؤهل:</strong> {selected.education}{selected.specialty ? ` (${selected.specialty})` : ""}</div>}
            <div style={S.detailRow}><strong>ساعات التطوع:</strong> {selected.hours}</div>
            <div style={S.detailRow}><strong>تاريخ الانضمام:</strong> {selected.joinDate}</div>
            {selected.skills && selected.skills.length > 0 && (
              <div style={S.detailRow}>
                <strong>المهارات:</strong>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>{selected.skills.map((sk, i) => <span key={i} style={S.skillTag}>{sk}</span>)}</div>
              </div>
            )}
            {selected.interests && selected.interests.length > 0 && (
              <div style={S.detailRow}>
                <strong>الاهتمامات:</strong>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>{selected.interests.map((it, i) => <span key={i} style={{ ...S.skillTag, background: `${C.teal}10`, color: C.teal }}>{it}</span>)}</div>
              </div>
            )}
            {selected.availability && selected.availability.length > 0 && (
              <div style={S.detailRow}>
                <strong>الأوقات المتاحة:</strong>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>{selected.availability.map((a, i) => <span key={i} style={{ ...S.skillTag, background: `${C.accent}10`, color: "#8b6914" }}>{a}</span>)}</div>
              </div>
            )}

            {/* Evaluations history */}
            {selected.evaluations && selected.evaluations.length > 0 && (
              <div>
                <strong style={{ color: C.primary }}>تقييمات سابقة ({selected.evaluations.length}):</strong>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                  {selected.evaluations.map(ev => (
                    <div key={ev.id} style={{ padding: 10, background: C.bg, borderRadius: 8, fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 600 }}>{ev.periodFrom} → {ev.periodTo}</span>
                        <span style={{ padding: "2px 10px", borderRadius: 6, fontWeight: 700, background: `${C.success}15`, color: C.success }}>
                          {ev.percentScore}% — {ev.recommendation}
                        </span>
                      </div>
                      {ev.evaluator && <p style={{ margin: "4px 0 0", color: C.textLight }}>المقيّم: {ev.evaluator} — {ev.date}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Star} onClick={() => { setEvalForm(INITIAL_EVAL); setShowEval(true); }}>تقييم المتطوع</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Evaluation Modal */}
      {showEval && selected && (() => {
        const { avg, percent } = computeAvg();
        return (
        <Modal title={`تقييم المتطوع — ${selected.name}`} onClose={() => { setShowEval(false); setEvalForm(INITIAL_EVAL); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ padding: 10, background: `${C.teal}08`, borderRadius: 8, fontSize: 13, color: C.primary }}>
              <strong>اسم المتطوع:</strong> {selected.name}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="فترة التقييم — من" type="date" required value={evalForm.periodFrom} onChange={v => setEvalForm({ ...evalForm, periodFrom: v })} />
              <Field label="فترة التقييم — إلى" type="date" required value={evalForm.periodTo} onChange={v => setEvalForm({ ...evalForm, periodTo: v })} />
            </div>

            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.primary }}>معايير التقييم</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="الالتزام بالمواعيد" type="select" options={RATING_LEVELS} value={evalForm.punctuality} onChange={v => setEvalForm({ ...evalForm, punctuality: v })} />
                <Field label="جودة العمل" type="select" options={RATING_LEVELS} value={evalForm.workQuality} onChange={v => setEvalForm({ ...evalForm, workQuality: v })} />
                <Field label="العمل الجماعي" type="select" options={RATING_LEVELS} value={evalForm.teamwork} onChange={v => setEvalForm({ ...evalForm, teamwork: v })} />
                <Field label="المبادرة" type="select" options={RATING_LEVELS} value={evalForm.initiative} onChange={v => setEvalForm({ ...evalForm, initiative: v })} />
                <Field label="المظهر والسلوك" type="select" options={RATING_LEVELS} value={evalForm.appearance} onChange={v => setEvalForm({ ...evalForm, appearance: v })} />
              </div>
            </div>

            {/* Auto-calculated overall */}
            <div style={{ padding: 14, background: `${C.success}08`, border: `1px solid ${C.success}30`, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>التقييم العام (محسوب تلقائياً):</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", gap: 2 }}>
                  {[1, 2, 3, 4].map(n => (
                    <Star key={n} size={20} fill={n <= Math.round(avg) ? C.accent : "none"} color={n <= Math.round(avg) ? C.accent : C.textLight} />
                  ))}
                </div>
                <span style={{ fontSize: 18, fontWeight: 700, color: percent >= 80 ? C.success : percent >= 60 ? C.teal : C.warning }}>{percent}%</span>
              </div>
            </div>

            <Field label="عدد الساعات في الفترة" type="number" placeholder="0" value={evalForm.hours} onChange={v => setEvalForm({ ...evalForm, hours: v })} />
            <Field label="الإنجازات البارزة" type="textarea" rows={2} placeholder="أبرز ما حققه..." value={evalForm.achievements} onChange={v => setEvalForm({ ...evalForm, achievements: v })} />
            <Field label="نقاط التحسين" type="textarea" rows={2} placeholder="نقاط للتطوير..." value={evalForm.improvements} onChange={v => setEvalForm({ ...evalForm, improvements: v })} />
            <Field label="التوصية" type="select" options={RECOMMENDATIONS} value={evalForm.recommendation} onChange={v => setEvalForm({ ...evalForm, recommendation: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="اسم المقيّم" placeholder="اسم من قام بالتقييم" value={evalForm.evaluator} onChange={v => setEvalForm({ ...evalForm, evaluator: v })} />
              <Field label="ملاحظات المقيّم" placeholder="ملاحظات أخرى..." value={evalForm.evaluatorNotes} onChange={v => setEvalForm({ ...evalForm, evaluatorNotes: v })} />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Star} onClick={handleSaveEval}>حفظ التقييم</Btn>
              <Btn variant="ghost" onClick={() => { setShowEval(false); setEvalForm(INITIAL_EVAL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
        );
      })()}

      {showAdd && (
        <Modal title="إضافة متطوع جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_VOL); }} width={780}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Section 1: Personal */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#059669", borderBottom: `2px solid #05966930`, paddingBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span>👤</span> البيانات الشخصية
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="الاسم الكامل" required placeholder="الاسم الثلاثي" value={form.name} onChange={v => setForm({ ...form, name: v })} />
                <Field label="رقم الهوية/الإقامة" placeholder="10xxxxxxxx" value={form.nationalId} onChange={v => setForm({ ...form, nationalId: v })} />
                <Field label="الجنسية" type="select" options={VOL_NATIONALITIES} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="تاريخ الميلاد" type="date" value={form.birthDate} onChange={v => setForm({ ...form, birthDate: v })} />
                <Field label="الجنس" type="select" options={VOL_GENDERS} value={form.gender} onChange={v => setForm({ ...form, gender: v })} />
                <Field label="الحالة الاجتماعية" type="select" options={VOL_MARITAL} value={form.maritalStatus} onChange={v => setForm({ ...form, maritalStatus: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="المؤهل التعليمي" type="select" options={VOL_EDUCATION} value={form.education} onChange={v => setForm({ ...form, education: v })} />
                <Field label="التخصص" placeholder="مثال: علوم حاسب" value={form.specialty} onChange={v => setForm({ ...form, specialty: v })} />
                <Field label="جهة العمل/الدراسة" placeholder="مثال: جامعة الملك سعود" value={form.workplace} onChange={v => setForm({ ...form, workplace: v })} />
              </div>
            </div>

            {/* Section 2: Contact */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span>📞</span> بيانات التواصل
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="رقم الجوال" required placeholder="05xxxxxxxx" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
                <Field label="البريد الإلكتروني" placeholder="email@example.com" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="المدينة" placeholder="بريدة" value={form.city} onChange={v => setForm({ ...form, city: v })} />
                <Field label="الحي" placeholder="حي الروضة" value={form.district} onChange={v => setForm({ ...form, district: v })} />
              </div>
            </div>

            {/* Section 3: Volunteering */}
            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: C.accent, borderBottom: `2px solid ${C.accent}30`, paddingBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span>🤝</span> بيانات التطوع
              </h4>
              <Field label="الفريق" type="select" options={VOL_TEAMS} value={form.team} onChange={v => setForm({ ...form, team: v })} />
              <Field label="المهارات (مفصولة بفواصل)" type="textarea" rows={2} placeholder="تنظيم، قيادة، تصميم، تصوير..." value={form.skills} onChange={v => setForm({ ...form, skills: v })} />
              <Field label="الخبرات التطوعية السابقة" type="textarea" rows={2} placeholder="اذكر تجاربك التطوعية السابقة..." value={form.priorExperience} onChange={v => setForm({ ...form, priorExperience: v })} />

              {/* Multi-select interests */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                  مجالات الاهتمام (متعدد) — {form.interests.length} مختار
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                  {VOL_INTERESTS.map(i => (
                    <label key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                      <input type="checkbox" checked={form.interests.includes(i)} onChange={() => toggleArrField("interests", i)} />
                      <span>{i}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Multi-select availability */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600, color: C.textLight }}>
                  الأوقات المتاحة (متعدد) — {form.availability.length} مختار
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, padding: 10, background: C.bg, borderRadius: 8 }}>
                  {VOL_AVAILABILITY.map(a => (
                    <label key={a} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                      <input type="checkbox" checked={form.availability.includes(a)} onChange={() => toggleArrField("availability", a)} />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="هل لديك رخصة قيادة؟" type="select" options={VOL_YESNO} value={form.hasDrivingLicense} onChange={v => setForm({ ...form, hasDrivingLicense: v })} />
                <Field label="هل لديك سيارة؟" type="select" options={VOL_YESNO} value={form.hasCar} onChange={v => setForm({ ...form, hasCar: v })} />
              </div>

              <Field label="ملاحظات" type="textarea" rows={2} placeholder="أي ملاحظات إضافية..." value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>حفظ المتطوع</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_VOL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Teams Tab ─── */
const INITIAL_TEAM = { name: "", description: "", leader: "", goals: "", targetMembers: "", area: "إفطار", notes: "" };
const TEAM_AREAS = ["إفطار", "مساجد", "جاليات", "إعلام", "نسائي", "طوارئ", "تعليم", "صيانة", "أخرى"];

function TeamsTab({ canEdit }) {
  const [teams, setTeams] = useState(DEMO_TEAMS);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_TEAM);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.name || !form.leader) {
      showToast("الاسم والقائد مطلوبان", "error");
      return;
    }
    const newT = {
      id: `tm${Date.now()}`,
      name: form.name,
      description: form.description || "",
      members: 0,
      totalHours: 0,
      activeTasks: 0,
      leader: form.leader,
      goals: form.goals,
      targetMembers: Number(form.targetMembers) || 0,
      area: form.area,
      notes: form.notes,
      color: "#7c3aed",
    };
    setTeams(prev => [...prev, newT]);
    setForm(INITIAL_TEAM);
    setShowAdd(false);
    showToast("تم إنشاء الفريق");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="عدد الفرق" value={teams.length} icon={Layers} color="#7c3aed" />
        <KPICard title="إجمالي الأعضاء" value={teams.reduce((s, t) => s + t.members, 0)} icon={Users} color="#059669" />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إنشاء فريق</Btn>
        </div>
      )}

      <div style={S.teamsGrid}>
        {teams.map(t => (
          <Card key={t.id} style={S.teamCard}>
            <div style={{ ...S.teamIcon, background: `${t.color}15` }}>
              <Layers size={24} color={t.color} />
            </div>
            <h4 style={{ margin: "12px 0 4px", fontSize: 16, fontWeight: 700, color: C.primary }}>{t.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: C.textLight, lineHeight: 1.6 }}>{t.description}</p>
            <div style={S.teamStats}>
              <span><Users size={13} /> {t.members} عضو</span>
              <span><Timer size={13} /> {t.totalHours} ساعة</span>
              <span><Zap size={13} /> {t.activeTasks} مهمة</span>
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: C.teal, fontWeight: 600 }}>
              القائد: {t.leader}
            </div>
          </Card>
        ))}
      </div>

      {showAdd && (
        <Modal title="إنشاء فريق تطوعي" onClose={() => { setShowAdd(false); setForm(INITIAL_TEAM); }} width={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم الفريق" required placeholder="مثال: فريق الإفطار" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="وصف الفريق" type="textarea" rows={3} placeholder="مهام ومسؤوليات الفريق..." value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="قائد الفريق" required placeholder="اسم القائد" value={form.leader} onChange={v => setForm({ ...form, leader: v })} />
              <Field label="مجال العمل" type="select" options={TEAM_AREAS} value={form.area} onChange={v => setForm({ ...form, area: v })} />
            </div>
            <Field label="أهداف الفريق" type="textarea" rows={3} placeholder="الأهداف الاستراتيجية والتشغيلية..." value={form.goals} onChange={v => setForm({ ...form, goals: v })} />
            <Field label="عدد الأعضاء المستهدف" type="number" placeholder="0" value={form.targetMembers} onChange={v => setForm({ ...form, targetMembers: v })} />
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Layers} onClick={handleSave}>إنشاء</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_TEAM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Opportunities Tab ─── */
const INITIAL_OPP = { title: "", category: "", description: "", date: "", location: "", needed: "" };

function OpportunitiesTab({ canEdit }) {
  const [opps, setOpps] = useState(DEMO_OPPORTUNITIES);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_OPP);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.title || !form.category || !form.date) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const newO = {
      id: `op${Date.now()}`,
      title: form.title,
      category: form.category,
      description: form.description || "",
      date: form.date,
      location: form.location || "—",
      needed: Number(form.needed) || 0,
      registered: 0,
      status: "open",
      color: "#7c3aed",
    };
    setOpps(prev => [...prev, newO]);
    setForm(INITIAL_OPP);
    setShowAdd(false);
    showToast("تم إنشاء الفرصة");
  }

  const OPP_STATUSES = [
    { key: "open", label: "مفتوح", color: C.success },
    { key: "full", label: "مكتمل العدد", color: C.warning },
    { key: "closed", label: "مغلق", color: C.textLight },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="الفرص المتاحة" value={opps.filter(o => o.status === "open").length} icon={Zap} color={C.success} />
        <KPICard title="إجمالي الفرص" value={opps.length} icon={Gift} color="#7c3aed" />
        <KPICard title="المتطوعون المطلوبون" value={opps.reduce((s, o) => s + o.needed, 0)} icon={Users} color="#059669" />
      </div>

      {canEdit && (
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">فرصة جديدة</Btn>
        </div>
      )}

      <div style={S.oppsGrid}>
        {opps.map(o => {
          const pct = Math.round((o.registered / o.needed) * 100);
          return (
            <Card key={o.id} style={S.oppCard}>
              <div style={{ ...S.oppStrip, background: o.color }} />
              <div style={{ padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600, background: `${o.color}12`, color: o.color }}>{o.category}</span>
                  <StatusBadge status={o.status} statuses={OPP_STATUSES} />
                </div>
                <h4 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.primary }}>{o.title}</h4>
                <p style={{ margin: 0, fontSize: 12, color: C.textLight, lineHeight: 1.6 }}>{o.description}</p>
                <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 12, color: C.textLight }}>
                  <span><Calendar size={12} /> {o.date}</span>
                  <span><MapPin size={12} /> {o.location}</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={S.progressBg}>
                    <div style={{ ...S.progressFill, width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? C.success : o.color }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11 }}>
                    <span style={{ color: C.textLight }}>{o.registered}/{o.needed} متطوع</span>
                    <span style={{ fontWeight: 700, color: pct >= 100 ? C.success : o.color }}>{pct}%</span>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {showAdd && (
        <Modal title="إنشاء فرصة تطوعية" onClose={() => { setShowAdd(false); setForm(INITIAL_OPP); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: تنظيم إفطار الصائمين" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <Field label="الفئة" type="select" options={["إفطار","صيانة","تعليم","توزيع","تنظيم فعاليات","إعلام","نظافة"]} required value={form.category} onChange={v => setForm({ ...form, category: v })} />
            <Field label="الوصف" type="textarea" placeholder="تفاصيل الفرصة ومتطلباتها..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" required value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="الموقع" placeholder="المكان" value={form.location} onChange={v => setForm({ ...form, location: v })} />
              <Field label="العدد المطلوب" type="number" placeholder="0" value={form.needed} onChange={v => setForm({ ...form, needed: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Zap} onClick={handleSave}>إنشاء</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_OPP); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Hours Tab ─── */
function HoursTab() {
  const topVolunteers = [...DEMO_VOLUNTEERS].sort((a, b) => b.hours - a.hours).slice(0, 8);
  const totalHours = DEMO_VOLUNTEERS.reduce((s, v) => s + v.hours, 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="إجمالي الساعات" value={totalHours.toLocaleString()} icon={Timer} color="#059669" />
        <KPICard title="متوسط الساعات" value={Math.round(totalHours / DEMO_VOLUNTEERS.length)} icon={TrendingUp} color={C.teal} />
        <KPICard title="متطوع متميز (100+ ساعة)" value={DEMO_VOLUNTEERS.filter(v => v.hours >= 100).length} icon={Award} color={C.accent} />
      </div>

      <Card style={{ padding: 20 }}>
        <h4 style={S.sectionTitle}><Award size={18} color={C.accent} /> ترتيب المتطوعين حسب الساعات</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {topVolunteers.map((v, i) => (
            <div key={v.id} style={S.hourRow}>
              <div style={{ ...S.rankBadge, background: i < 3 ? `${C.accent}20` : C.bg, color: i < 3 ? C.accent : C.textLight }}>{i + 1}</div>
              <div style={S.hourAvatar}>{v.name.charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.primary }}>{v.name}</span>
                <span style={{ fontSize: 12, color: C.textLight, marginRight: 8 }}>({v.team})</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 120, height: 8, borderRadius: 4, background: C.bg, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 4, width: `${(v.hours / topVolunteers[0].hours) * 100}%`, background: i < 3 ? C.accent : C.teal }} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.primary, minWidth: 50, textAlign: "left" }}>{v.hours}h</span>
              </div>
              {v.hours >= 100 && <Star size={16} color={C.accent} />}
            </div>
          ))}
        </div>
      </Card>

      {/* Monthly distribution */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={S.sectionTitle}><Calendar size={18} color="#059669" /> توزيع الساعات الشهري</h4>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 10 }}>
          {MONTHLY_HOURS.map((m, i) => (
            <div key={i} style={S.monthBox}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>{m.month}</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>{m.hours}</span>
              <span style={{ fontSize: 10, color: C.textLight }}>ساعة</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ═════ Demo Data ═════ */
const DEMO_VOLUNTEERS = [
  { id: "v1", name: "سعود الحربي", team: "فريق الإفطار", phone: "0551111111", email: "s1@vol.com", hours: 185, joinDate: "1444/01", status: "active", skills: ["تنظيم","قيادة","طبخ"] },
  { id: "v2", name: "عبدالعزيز المالكي", team: "فريق المساجد", phone: "0552222222", email: "s2@vol.com", hours: 142, joinDate: "1444/03", status: "active", skills: ["صيانة","كهرباء","نظافة"] },
  { id: "v3", name: "محمد الشهري", team: "فريق الإفطار", phone: "0553333333", email: "s3@vol.com", hours: 128, joinDate: "1444/06", status: "active", skills: ["تنظيم","توزيع","سيارات"] },
  { id: "v4", name: "فيصل العسيري", team: "فريق الجاليات", phone: "0554444444", email: "s4@vol.com", hours: 115, joinDate: "1445/01", status: "active", skills: ["ترجمة","تدريس","إنجليزي"] },
  { id: "v5", name: "نايف القحطاني", team: "فريق الإعلام", phone: "0555555555", email: "s5@vol.com", hours: 98, joinDate: "1445/02", status: "active", skills: ["تصوير","مونتاج","تصميم"] },
  { id: "v6", name: "خالد الرشيدي", team: "فريق المساجد", phone: "0556666666", email: "s6@vol.com", hours: 87, joinDate: "1445/04", status: "active", skills: ["سباكة","صيانة"] },
  { id: "v7", name: "عمر الزهراني", team: "فريق الإفطار", phone: "0557777777", email: "s7@vol.com", hours: 76, joinDate: "1445/06", status: "active", skills: ["توزيع","تنظيم"] },
  { id: "v8", name: "بندر السبيعي", team: "فريق الطوارئ", phone: "0558888888", email: "s8@vol.com", hours: 65, joinDate: "1445/08", status: "active", skills: ["إسعاف","قيادة","تنظيم"] },
  { id: "v9", name: "ماجد العتيبي", team: "فريق النسائي", phone: "0559999999", email: "s9@vol.com", hours: 52, joinDate: "1446/01", status: "active", skills: ["نقل","تنسيق"] },
  { id: "v10", name: "طارق الغامدي", team: "فريق الجاليات", phone: "0550000000", email: "s10@vol.com", hours: 45, joinDate: "1446/02", status: "inactive", skills: ["ترجمة","أردية"] },
];

const DEMO_TEAMS = [
  { id: "tm1", name: "فريق الإفطار", description: "تنظيم وتوزيع وجبات الإفطار في المساجد وسكن العمال", members: 45, totalHours: 2800, activeTasks: 12, leader: "سعود الحربي", color: "#ea580c" },
  { id: "tm2", name: "فريق المساجد", description: "صيانة ونظافة وتجهيز المساجد للصلوات والمناسبات", members: 28, totalHours: 1600, activeTasks: 8, leader: "عبدالعزيز المالكي", color: "#0891b2" },
  { id: "tm3", name: "فريق الجاليات", description: "مرافقة الدعاة وترجمة والتواصل مع الجاليات", members: 22, totalHours: 1200, activeTasks: 6, leader: "فيصل العسيري", color: "#2563eb" },
  { id: "tm4", name: "فريق الإعلام", description: "تصوير وتوثيق الفعاليات وإدارة محتوى السوشيال ميديا", members: 15, totalHours: 800, activeTasks: 5, leader: "نايف القحطاني", color: "#c026d3" },
  { id: "tm5", name: "فريق النسائي", description: "دعم برامج القسم النسائي والحلقات والفعاليات", members: 18, totalHours: 700, activeTasks: 4, leader: "منسقة التطوع", color: "#db2777" },
  { id: "tm6", name: "فريق الطوارئ", description: "الاستجابة للحالات الطارئة والتنظيم في الفعاليات الكبرى", members: 12, totalHours: 450, activeTasks: 2, leader: "بندر السبيعي", color: "#dc2626" },
];

const DEMO_OPPORTUNITIES = [
  { id: "op1", title: "تنظيم إفطار يومي — رمضان", category: "إفطار", description: "المساعدة في تجهيز وتوزيع وجبات الإفطار في 12 موقعاً يومياً", date: "رمضان 1446", location: "متعدد", needed: 60, registered: 52, status: "open", color: "#ea580c" },
  { id: "op2", title: "صيانة مسجد البر", category: "صيانة", description: "المشاركة في أعمال الصيانة والنظافة والدهان", date: "1446/10/05", location: "مسجد البر", needed: 15, registered: 15, status: "full", color: "#0891b2" },
  { id: "op3", title: "ملتقى الجاليات الثالث", category: "تنظيم فعاليات", description: "تنظيم واستقبال وإرشاد الحضور في الملتقى", date: "1446/07/15", location: "قاعة الملك فهد", needed: 30, registered: 18, status: "open", color: "#2563eb" },
  { id: "op4", title: "توزيع حقائب شتوية", category: "توزيع", description: "توزيع 2000 حقيبة شتوية على العمال في المنطقة الصناعية", date: "1446/04/10", location: "المنطقة الصناعية", needed: 25, registered: 25, status: "closed", color: "#d97706" },
  { id: "op5", title: "تصوير حملة رمضان", category: "إعلام", description: "تصوير فوتوغرافي وفيديو لأنشطة الجمعية في رمضان", date: "رمضان 1446", location: "متعدد", needed: 8, registered: 6, status: "open", color: "#c026d3" },
  { id: "op6", title: "تدريس لغة عربية للجاليات", category: "تعليم", description: "تدريس اللغة العربية في حلقات أسبوعية للجاليات غير الناطقة بالعربية", date: "مستمر", location: "مسجد التوحيد", needed: 10, registered: 7, status: "open", color: "#059669" },
];

const MONTHLY_HOURS = [
  { month: "محرم", hours: 320 }, { month: "صفر", hours: 280 }, { month: "ربيع1", hours: 310 },
  { month: "ربيع2", hours: 260 }, { month: "جمادى1", hours: 340 }, { month: "جمادى2", hours: 290 },
  { month: "رجب", hours: 380 }, { month: "شعبان", hours: 420 }, { month: "رمضان", hours: 850 },
  { month: "شوال", hours: 300 }, { month: "ذوالقعدة", hours: 270 }, { month: "ذوالحجة", hours: 350 },
];

/* ═════ Styles ═════ */
const S = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  volGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 },
  volCard: { padding: 18, cursor: "pointer" },
  volTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  volAvatar: { width: 46, height: 46, borderRadius: 12, background: `linear-gradient(135deg, #059669, #34d399)`, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 18 },
  skillTag: { padding: "3px 8px", background: `#05966912`, color: "#059669", borderRadius: 6, fontSize: 11, fontWeight: 600 },
  volStats: { display: "flex", gap: 20, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight },
  starBadge: { display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${C.accent}15`, color: C.accent },

  teamsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  teamCard: { padding: 20 },
  teamIcon: { width: 48, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" },
  teamStats: { display: "flex", gap: 16, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight },

  oppsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  oppCard: { padding: 0, overflow: "hidden" },
  oppStrip: { height: 4, width: "100%" },
  progressBg: { height: 8, borderRadius: 4, background: C.bg, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 4, transition: "width 0.5s" },

  hourRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.bg}` },
  rankBadge: { width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
  hourAvatar: { width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, #059669, #34d399)`, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  monthBox: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` },
};
